import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  Box,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  AutoAwesome as AssistantIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Folder as FolderIcon,
  ConfirmationNumber as TicketIcon,
  Comment as CommentIcon,
  Description as DocumentIcon,
  Domain as DomainIcon,
  Help as HelpIcon,
  ReceiptLong as PurchaseOrderIcon,
  Groups as GroupsIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  SwapHoriz as SwapHorizIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';
import { useAppMode } from '../../contexts/AppModeContext';
import { useImpersonatedAuth } from '../../hooks/useImpersonatedAuth';
import { canSeeAssistantEntry } from '../../utils/assistantAccess';
import SherlockIcon from './SherlockIcon';

interface SearchResult {
  id: string;
  type: 'client' | 'project' | 'task' | 'task_template' | 'ticket' | 'user' | 'comment' | 'document' | 'company' | 'article' | 'purchase_order' | 'department';
  title: string;
  subtitle?: string;
  description?: string;
  metadata?: string;
  status?: string;
}

type CommandKind = 'action' | 'navigation' | 'mode' | 'result';

interface CommandItem {
  id: string;
  kind: CommandKind;
  label: string;
  subtitle?: string;
  group: string;
  icon: React.ReactNode;
  keywords?: string;
  onSelect: () => void;
  trailing?: string;
}

interface Props {
  themeMode?: string;
  onToggleTheme?: () => void;
  onShowShortcuts?: () => void;
  onLogout?: () => void;
}

const resultIcon = (type: SearchResult['type']) => {
  switch (type) {
    case 'client': return <BusinessIcon />;
    case 'project': return <FolderIcon />;
    case 'task': return <AssignmentIcon />;
    case 'task_template': return <AssignmentIcon />;
    case 'ticket': return <TicketIcon />;
    case 'user': return <PersonIcon />;
    case 'comment': return <CommentIcon />;
    case 'document': return <DocumentIcon />;
    case 'company': return <DomainIcon />;
    case 'article': return <HelpIcon />;
    case 'purchase_order': return <PurchaseOrderIcon />;
    case 'department': return <GroupsIcon />;
    default: return <SearchIcon />;
  }
};

const navigateForResult = (result: SearchResult, navigate: (path: string, options?: { state?: unknown }) => void) => {
  switch (result.type) {
    case 'client': navigate(`/clients/${result.id}`); break;
    case 'project': navigate(`/projects/${result.id}`); break;
    case 'task': navigate(`/tasks/${result.id}`); break;
    // /task-templates/:id edit is crm:manage-gated; the list is open to all.
    case 'task_template': navigate('/task-templates'); break;
    case 'ticket': navigate(`/tickets/${result.id}`); break;
    case 'user': navigate(`/users/${result.id}`); break;
    case 'company': navigate(`/companies/${result.id}`); break;
    case 'article': navigate(`/knowledge-base/${result.id}`); break;
    // No per-PO detail route; hand the id to the component-gated list so it
    // expands + scrolls to the PO (and clears its default date filter).
    case 'purchase_order': navigate('/inventory/purchase-orders', { state: { expandPOId: result.id } }); break;
    case 'department': navigate('/departments'); break;
    case 'comment':
    case 'document':
      if (result.metadata) {
        const [parentType, parentId] = result.metadata.split(':');
        navigate(`/${parentType}s/${parentId}`);
      }
      break;
  }
};

const CommandPalette: React.FC<Props> = ({ themeMode, onToggleTheme, onLogout, onShowShortcuts }) => {
  const navigate = useNavigate();
  const { mode: appMode, setMode: setAppMode } = useAppMode();
  const showAssistant = canSeeAssistantEntry(useImpersonatedAuth().user);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebounce(query, 250);

  // Cmd-K / Ctrl-K opens the palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isToggle = (e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey);
      if (isToggle) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Reset state on close.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveIdx(0);
    }
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  // Always-available actions.
  const staticCommands = useMemo<CommandItem[]>(() => {
    const items: CommandItem[] = [
      { id: 'nav:my-assignments', kind: 'navigation', group: 'Navigate', label: 'My Assignments', icon: <ArrowForwardIcon />, keywords: 'home dashboard', onSelect: () => navigate('/my-assignments') },
      { id: 'nav:calendar', kind: 'navigation', group: 'Navigate', label: 'Calendar', icon: <ArrowForwardIcon />, keywords: 'calendar schedule due dates', onSelect: () => navigate('/calendar') },
      { id: 'nav:projects', kind: 'navigation', group: 'Navigate', label: 'Projects', icon: <FolderIcon />, keywords: 'project list', onSelect: () => navigate('/projects') },
      { id: 'nav:tasks', kind: 'navigation', group: 'Navigate', label: 'Tasks', icon: <AssignmentIcon />, keywords: 'task list', onSelect: () => navigate('/tasks') },
      { id: 'nav:tickets', kind: 'navigation', group: 'Navigate', label: 'Tickets', icon: <TicketIcon />, keywords: 'support ticket list', onSelect: () => navigate('/tickets') },
      { id: 'nav:clients', kind: 'navigation', group: 'Navigate', label: 'Clients', icon: <BusinessIcon />, keywords: 'client list customers', onSelect: () => navigate('/clients') },
      // AI Assistant — same visibility as its top-bar icon.
      ...(showAssistant ? [{ id: 'nav:assistant', kind: 'navigation' as const, group: 'Navigate', label: 'AI Assistant', icon: <AssistantIcon />, keywords: 'ai assistant sales lead dictate voice prospect note task ticket', onSelect: () => navigate('/assistant') }] : []),
      { id: 'nav:documents', kind: 'navigation', group: 'Navigate', label: 'Documents', icon: <DocumentIcon />, keywords: 'files documents', onSelect: () => navigate('/documents') },
      { id: 'nav:analytics', kind: 'navigation', group: 'Navigate', label: 'Analytics', icon: <ArrowForwardIcon />, keywords: 'reports analytics dashboard', onSelect: () => navigate('/analytics') },

      { id: 'create:project', kind: 'action', group: 'Create', label: 'New project', icon: <AddIcon />, keywords: 'new create project add', onSelect: () => navigate('/projects/new') },
      { id: 'create:task', kind: 'action', group: 'Create', label: 'New task', icon: <AddIcon />, keywords: 'new create task add', onSelect: () => navigate('/tasks/new') },
      { id: 'create:ticket', kind: 'action', group: 'Create', label: 'New ticket', icon: <AddIcon />, keywords: 'new create ticket add', onSelect: () => navigate('/tickets/new') },

      { id: 'mode:crm', kind: 'mode', group: 'Switch portal', label: 'Switch to CRM', icon: <SwapHorizIcon />, keywords: 'portal mode crm', onSelect: () => setAppMode('crm') },
      { id: 'mode:lis', kind: 'mode', group: 'Switch portal', label: 'Switch to LIS', icon: <SwapHorizIcon />, keywords: 'portal mode lis laboratory', onSelect: () => setAppMode('lis') },
      { id: 'mode:billing', kind: 'mode', group: 'Switch portal', label: 'Switch to Billing', icon: <SwapHorizIcon />, keywords: 'portal mode billing', onSelect: () => setAppMode('billing') },
      { id: 'mode:vob', kind: 'mode', group: 'Switch portal', label: 'Switch to VOB', icon: <SwapHorizIcon />, keywords: 'portal mode vob verification of benefits', onSelect: () => setAppMode('vob') },
      { id: 'mode:integrations', kind: 'mode', group: 'Switch portal', label: 'Switch to Integrations', icon: <SwapHorizIcon />, keywords: 'portal mode integrations google workspace meraki email', onSelect: () => setAppMode('integrations') },
      { id: 'mode:tools', kind: 'mode', group: 'Switch portal', label: 'Switch to Tools', icon: <SwapHorizIcon />, keywords: 'portal mode tools s3 browser storage admin', onSelect: () => setAppMode('tools') }
    ];

    if (onToggleTheme) {
      items.push({
        id: 'pref:theme',
        kind: 'action',
        group: 'Preferences',
        // toggleTheme cycles light → dark → orange → slate → light.
        label: `Cycle theme (now: ${themeMode ? themeMode[0].toUpperCase() + themeMode.slice(1) : 'Light'})`,
        icon: themeMode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />,
        keywords: 'theme dark light orange slate mode cycle',
        onSelect: () => onToggleTheme()
      });
    }

    if (onLogout) {
      items.push({
        id: 'auth:logout',
        kind: 'action',
        group: 'Preferences',
        label: 'Log out',
        icon: <LogoutIcon />,
        keywords: 'log out signout sign out',
        onSelect: () => onLogout()
      });
    }

    if (onShowShortcuts) {
      items.push({
        id: 'help:shortcuts',
        kind: 'action',
        group: 'Help',
        label: 'Keyboard shortcuts',
        icon: <SearchIcon />,
        keywords: 'help shortcuts keys keyboard',
        onSelect: () => onShowShortcuts()
      });
    }

    return items;
  }, [navigate, setAppMode, onToggleTheme, onLogout, themeMode, onShowShortcuts, showAssistant]);

  // Fuzzy-ish local match for static commands.
  const filteredStatic = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return staticCommands;
    return staticCommands.filter((c) => {
      const hay = `${c.label} ${c.group} ${c.keywords || ''}`.toLowerCase();
      return q.split(/\s+/).every((token) => hay.includes(token));
    });
  }, [staticCommands, query]);

  // Server-side search for entities (when query >= 2 chars).
  const { data: results, isLoading: isSearching } = useQuery({
    queryKey: ['command-palette-search', debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) return [] as SearchResult[];
      const response = await api.get('/search', { params: { q: debouncedQuery } });
      return (response.data?.data || []) as SearchResult[];
    },
    enabled: open && debouncedQuery.length >= 2,
    staleTime: 30_000
  });

  const resultCommands = useMemo<CommandItem[]>(() => {
    return (results || []).map((r) => ({
      id: `result:${r.type}:${r.id}`,
      kind: 'result',
      group: 'Results',
      label: r.title,
      subtitle: r.subtitle || r.description,
      icon: resultIcon(r.type),
      onSelect: () => navigateForResult(r, navigate),
      trailing: r.type
    }));
  }, [results, navigate]);

  const items = useMemo<CommandItem[]>(() => [...filteredStatic, ...resultCommands], [filteredStatic, resultCommands]);

  // Clamp active index when items change.
  useEffect(() => {
    setActiveIdx((idx) => Math.min(idx, Math.max(items.length - 1, 0)));
  }, [items]);

  // Scroll the active item into view.
  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector<HTMLElement>(`[data-cmd-idx="${activeIdx}"]`);
    if (active) active.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((idx) => (items.length ? (idx + 1) % items.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((idx) => (items.length ? (idx - 1 + items.length) % items.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[activeIdx];
      if (item) {
        item.onSelect();
        close();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  // Group items by their `group` field while preserving order.
  const grouped = useMemo(() => {
    const order: string[] = [];
    const buckets = new Map<string, CommandItem[]>();
    for (const it of items) {
      if (!buckets.has(it.group)) {
        buckets.set(it.group, []);
        order.push(it.group);
      }
      buckets.get(it.group)!.push(it);
    }
    return order.map((g) => ({ group: g, items: buckets.get(g)! }));
  }, [items]);

  let runningIdx = 0;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2, mt: 8, alignSelf: 'flex-start' } }}
    >
      <Paper elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <SherlockIcon sx={{ mr: 1 }} tooltip />
          <InputBase
            inputRef={inputRef}
            autoFocus
            fullWidth
            placeholder="Type a command or search…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            onKeyDown={handleKeyDown}
            sx={{ fontSize: '1rem' }}
          />
          {isSearching && <CircularProgress size={18} sx={{ ml: 1 }} />}
          <Box
            component="kbd"
            sx={{
              ml: 1,
              px: 0.75,
              py: 0.25,
              fontSize: '0.7rem',
              border: 1,
              borderColor: 'divider',
              borderRadius: 0.5,
              color: 'text.secondary',
              fontFamily: 'monospace'
            }}
          >
            esc
          </Box>
        </Box>

        <Box ref={listRef} sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {grouped.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {query.length >= 2 ? `No matches for "${query}"` : 'No commands available'}
              </Typography>
            </Box>
          ) : (
            grouped.map((g, gIdx) => (
              <React.Fragment key={g.group}>
                {gIdx > 0 && <Divider />}
                <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {g.group} — {appMode.toUpperCase()}
                  </Typography>
                </Box>
                <List dense sx={{ py: 0 }}>
                  {g.items.map((item) => {
                    const idx = runningIdx++;
                    const isActive = idx === activeIdx;
                    return (
                      <ListItem key={item.id} disablePadding data-cmd-idx={idx}>
                        <ListItemButton
                          selected={isActive}
                          onClick={() => { item.onSelect(); close(); }}
                          onMouseEnter={() => setActiveIdx(idx)}
                          sx={{ py: 0.75 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                          <ListItemText
                            primary={item.label}
                            secondary={item.subtitle}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                          {item.trailing && (
                            <Chip size="small" label={item.trailing} sx={{ height: 20, fontSize: '0.65rem' }} />
                          )}
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </React.Fragment>
            ))
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 0.75, borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary">
            <Box component="kbd" sx={{ px: 0.5, py: 0.25, fontSize: '0.65rem', border: 1, borderColor: 'divider', borderRadius: 0.5, fontFamily: 'monospace' }}>↑↓</Box>{' '}
            navigate{'  '}
            <Box component="kbd" sx={{ px: 0.5, py: 0.25, fontSize: '0.65rem', border: 1, borderColor: 'divider', borderRadius: 0.5, fontFamily: 'monospace' }}>↵</Box>{' '}
            select
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <Box component="kbd" sx={{ px: 0.5, py: 0.25, fontSize: '0.65rem', border: 1, borderColor: 'divider', borderRadius: 0.5, fontFamily: 'monospace' }}>⌘K</Box>{' '}
            to toggle
          </Typography>
        </Box>
      </Paper>
    </Dialog>
  );
};

export default CommandPalette;
